import {
  cloneElement,
  isValidElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type {
  HTMLAttributes,
  Key,
  PointerEvent as ReactPointerEvent,
  KeyboardEvent as ReactKeyboardEvent,
  ReactElement,
  ReactNode,
  RefObject,
} from 'react';
import { DragDropProvider } from '@dnd-kit/react';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { RotateCcw } from 'lucide-react';
import { useIsDesktop } from '../../hooks/use-is-desktop';
import { useTruncationTooltip } from '../../hooks/use-truncation-tooltip';
import {
  isDefaultTableLayout,
  resetTablePreferences,
  setTableColumnOrder,
  setTableColumnWidth,
  useTablePreferences,
} from '../../hooks/use-table-preferences';
import { cn } from '../../utils';
import { alignClass, DataTableColumnHeader } from './data-table-column-header';
import type { DataTableLabels } from './data-table-column-header';
import {
  clampColumnWidth,
  moveColumn,
  normalizeColumnWidth,
  reconcileColumnOrder,
  reconcileColumnWidths,
  resolveColumnWidths,
} from './data-table-layout';
import type {
  DataTableColumn,
  DataTableFilterLabels,
  DataTableFilterState,
  DataTableSortDirection,
} from './data-table-filters';

const DEFAULT_THRESHOLD = 60;
const DEFAULT_ROW_HEIGHT = 49;
const DEFAULT_CARD_HEIGHT = 116;
const OVERSCAN = 8;
const END_REACHED_SLACK = 10;
/** One arrow key on a resize handle; Shift multiplies it. */
const RESIZE_STEP_PX = 16;
const RESIZE_COARSE_STEP_PX = 64;

interface DataTableBaseProps<T> {
  items: T[];
  getItemKey: (item: T) => string;
  renderCard: (item: T) => ReactNode;
  /** True when the data source is empty, rather than filters matching nothing. */
  hasNoData: boolean;
  emptyMessage: string;
  noResultsMessage: string;
  virtualizeThreshold?: number;
  estimateRowHeight?: number;
  estimateCardHeight?: number;
  maxHeight?: string;
  onEndReached?: () => void;
  /**
   * Clip cells to one line and reveal the full text on hover. On by default —
   * a row whose height depends on its longest cell cannot be virtualized well,
   * and a wrapped cell is what column resizing exists to avoid.
   */
  truncateCells?: boolean;
}

interface DataTableColumnsProps<T, K extends string = string> {
  columns: DataTableColumn<T, K>[];
  renderRow?: never;
  header?: never;
  columnCount?: never;
  columnWidths?: never;

  /**
   * Enables persistence of this table's column order and widths.
   *
   * One `localStorage` key per id, so it must be unique across the app — two
   * tables sharing an id would overwrite each other on every drag. Resizing and
   * reordering still work without it; they just do not survive a reload.
   *
   * Setting it also puts a reset control in the last header cell once the
   * reader has changed something, so leave that column ~36px of slack (or make
   * it the one with an empty label).
   */
  tableId?: string;
  /** Required for the drag grips, resize handles and reset control to render. */
  labels?: DataTableLabels;
  rowProps?: (item: T) => HTMLAttributes<HTMLTableRowElement>;

  sortColumn?: K;
  sortDirection?: DataTableSortDirection;
  onSort?: (column: K) => void;
  /** Suppresses every sort button — e.g. while a search re-ranks the rows. */
  sortDisabled?: boolean;
  filters?: DataTableFilterState;
  onFiltersChange?: (filters: DataTableFilterState) => void;
  filterLabels?: DataTableFilterLabels;
}

export type DataTableProps<
  T,
  K extends string = string,
> = DataTableBaseProps<T> & DataTableColumnsProps<T, K>;

export function DataTable<T, K extends string = string>({
  columns,
  tableId,
  labels,
  rowProps,
  sortColumn,
  sortDirection,
  onSort,
  sortDisabled,
  filters,
  onFiltersChange,
  filterLabels,
  truncateCells = true,
  ...shell
}: DataTableBaseProps<T> & DataTableColumnsProps<T, K>) {
  const stored = useTablePreferences(tableId);
  /**
   * The in-session copy of the layout.
   *
   * It carries the whole layout when there is no `tableId` to persist to, and
   * during a resize it holds the dragged column's width from the moment the
   * pointer goes down — which is what keeps the column that absorbs the
   * leftover space from changing under the reader mid-drag.
   */
  const [localOrder, setLocalOrder] = useState<string[] | null>(null);
  const [localWidths, setLocalWidths] = useState<Record<string, number>>({});
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [activeResizeId, setActiveResizeId] = useState<string | null>(null);
  /** Bumped when a drag or a resize starts, to close any open filter panel. */
  const [interactionToken, setInteractionToken] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  /** The width the pointer is currently at, read once when the drag commits. */
  const draggedWidthRef = useRef(0);

  const visible = columns.filter((column) => !column.hidden);
  const order = reconcileColumnOrder(localOrder ?? stored.order, visible);
  const byId = new Map(visible.map((column) => [column.id, column]));
  const ordered = order.flatMap((id) => {
    const column = byId.get(id);
    return column ? [column] : [];
  });
  const widths = {
    ...reconcileColumnWidths(stored.widths, visible),
    ...localWidths,
  };
  /** dnd-kit indexes within the movable subsequence, not the rendered one. */
  const movable = ordered.filter(
    (column) => column.reorderable ?? column.pinned == null,
  );
  const movableIndex = new Map(
    movable.map((column, index) => [column.id, index]),
  );
  const hasCustomLayout =
    localOrder !== null ||
    Object.keys(localWidths).length > 0 ||
    !isDefaultTableLayout(stored);

  function persistWidth(columnId: string, width: number | null) {
    if (tableId) setTableColumnWidth(tableId, columnId, width);
    setLocalWidths((current) => {
      const next = { ...current };
      // With a `tableId` the store is the source of truth, so the session copy
      // only ever holds a width mid-drag: drop it either way once committed.
      if (tableId || width === null) delete next[columnId];
      else next[columnId] = width;
      return next;
    });
  }

  /**
   * Resizes by writing `col.style.width` straight to the DOM for the duration
   * of the drag, and commits to React once on release: a `setState` per pointer
   * move would re-render every visible row of the table.
   */
  function handleResizeStart(
    column: DataTableColumn<T, K>,
    index: number,
    event: ReactPointerEvent<HTMLElement>,
  ) {
    if (event.button !== 0) return;
    const grip = event.currentTarget;
    const cell = grip.closest('th');
    const tables = [...(containerRef.current?.querySelectorAll('table') ?? [])];
    if (!cell || tables.length === 0) return;
    event.preventDefault();
    grip.setPointerCapture(event.pointerId);

    const startX = event.clientX;
    const startWidth = cell.getBoundingClientRect().width;
    const startTotals = tables.map(
      (table) => table.getBoundingClientRect().width,
    );
    draggedWidthRef.current = clampColumnWidth(startWidth, column);

    // Pin the column at the width it is already rendering at, before anything
    // moves: it stops being the column that absorbs the leftover space, and the
    // layout that replaces it is pixel-for-pixel the one on screen.
    setLocalWidths((current) => ({
      ...current,
      [column.id]: draggedWidthRef.current,
    }));
    setActiveResizeId(column.id);
    setInteractionToken((token) => token + 1);

    const onMove = (moveEvent: PointerEvent) => {
      const width = clampColumnWidth(
        startWidth + (moveEvent.clientX - startX),
        column,
      );
      draggedWidthRef.current = width;
      tables.forEach((table, tableIndex) => {
        const col = table.querySelectorAll('col')[index];
        if (col instanceof HTMLElement) col.style.width = `${width}px`;
        table.style.width = `${startTotals[tableIndex] - startWidth + width}px`;
      });
    };
    const onEnd = () => {
      grip.removeEventListener('pointermove', onMove);
      grip.removeEventListener('pointerup', onEnd);
      grip.removeEventListener('pointercancel', onEnd);
      setActiveResizeId(null);
      persistWidth(column.id, draggedWidthRef.current);
    };
    grip.addEventListener('pointermove', onMove);
    grip.addEventListener('pointerup', onEnd);
    grip.addEventListener('pointercancel', onEnd);
  }

  /** Without this the feature fails WCAG 2.1.1, so it is not optional. */
  function handleResizeKeyDown(
    column: DataTableColumn<T, K>,
    event: ReactKeyboardEvent<HTMLElement>,
  ) {
    if (event.key === 'Home') {
      event.preventDefault();
      setInteractionToken((token) => token + 1);
      persistWidth(column.id, null);
      return;
    }
    const step = event.shiftKey ? RESIZE_COARSE_STEP_PX : RESIZE_STEP_PX;
    const delta =
      event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0;
    if (delta === 0) return;
    const cell = event.currentTarget.closest('th');
    if (!cell) return;
    event.preventDefault();
    setInteractionToken((token) => token + 1);
    persistWidth(
      column.id,
      clampColumnWidth(cell.getBoundingClientRect().width + delta, column),
    );
  }

  function handleDragStart(event: DragStartEvent) {
    setActiveDragId(String(event.operation.source?.id ?? ''));
    setInteractionToken((token) => token + 1);
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveDragId(null);
    const { source, target } = event.operation;
    if (event.canceled || !source || !target || source.id === target.id) return;
    const next = moveColumn(order, String(source.id), String(target.id));
    if (next === order) return;
    if (tableId) {
      setTableColumnOrder(tableId, next);
      setLocalOrder(null);
    } else {
      setLocalOrder(next);
    }
  }

  function reset() {
    if (tableId) resetTablePreferences(tableId);
    setLocalOrder(null);
    setLocalWidths({});
  }

  const header = (
    <tr className="group/head border-b-2 border-border bg-muted/50">
      {ordered.map((column, index) => (
        <DataTableColumnHeader<K>
          key={column.id}
          column={column}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={sortDisabled ? undefined : onSort}
          filters={filters}
          onFiltersChange={onFiltersChange}
          filterLabels={filterLabels}
          labels={labels}
          sortableIndex={labels ? movableIndex.get(column.id) : undefined}
          activeSortableIndex={
            activeDragId ? movableIndex.get(activeDragId) : undefined
          }
          interactionToken={interactionToken}
          onResizeStart={(event) => handleResizeStart(column, index, event)}
          onResizeKeyDown={(event) => handleResizeKeyDown(column, event)}
          onResizeReset={() => persistWidth(column.id, null)}
          reserveEndSpace={
            index === ordered.length - 1 && Boolean(labels) && hasCustomLayout
          }
        >
          {index === ordered.length - 1 && labels && hasCustomLayout && (
            <button
              type="button"
              aria-label={labels.resetColumns}
              title={labels.resetColumns}
              onClick={reset}
              className="absolute right-1.5 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-lg text-muted-foreground hover:bg-background hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <RotateCcw className="size-3.5" />
            </button>
          )}
        </DataTableColumnHeader>
      ))}
    </tr>
  );

  const renderRow = (item: T) => {
    const row = rowProps?.(item);
    return (
      <tr
        {...row}
        className={cn(
          'border-b border-border/50 transition-colors last:border-b-0',
          row?.className,
        )}
      >
        {ordered.map((column) => {
          const extra = column.cellProps?.(item);
          const cellClassName =
            typeof column.cellClassName === 'function'
              ? column.cellClassName(item)
              : column.cellClassName;
          return (
            <td
              key={column.id}
              {...extra}
              data-truncate={column.truncate === false ? 'off' : undefined}
              className={cn(
                'px-4 py-3',
                column.align && alignClass(column.align),
                cellClassName,
                extra?.className,
              )}
            >
              {column.cell(item)}
            </td>
          );
        })}
      </tr>
    );
  };

  return (
    <DragDropProvider onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <TableShell
        {...shell}
        header={header}
        renderRow={renderRow}
        columnCount={ordered.length}
        truncateCells={truncateCells}
        containerRef={containerRef}
        measureRef={measureRef}
        remeasureToken={`${order.join()}|${JSON.stringify(widths)}`}
        columnWidths={ordered.map((column) =>
          typeof column.width === 'string' ? column.width : undefined,
        )}
        columnLayout={{
          base: ordered.map(
            (column) => widths[column.id] ?? normalizeColumnWidth(column.width),
          ),
          userSized: ordered.map((column) => widths[column.id] !== undefined),
          pinnedEnd: ordered.map((column) => column.pinned === 'end'),
          activeResizeIndex: activeResizeId
            ? ordered.findIndex((column) => column.id === activeResizeId)
            : undefined,
        }}
      />
    </DragDropProvider>
  );
}

interface ColumnLayoutInput {
  base: (number | null)[];
  userSized?: boolean[];
  pinnedEnd?: boolean[];
  activeResizeIndex?: number;
}

interface TableShellProps<T> extends DataTableBaseProps<T> {
  renderRow: (item: T) => ReactElement;
  header: ReactNode;
  columnCount: number;
  /** Fallback classes for the colgroup, until the container is measured. */
  columnWidths: (string | undefined)[];
  columnLayout: ColumnLayoutInput;
  containerRef?: RefObject<HTMLDivElement | null>;
  measureRef?: RefObject<HTMLDivElement | null>;
  /** Any change drops the virtualizer's cached row heights. */
  remeasureToken?: string;
}

function TableShell<T>({
  items,
  getItemKey,
  renderRow,
  renderCard,
  columnCount,
  hasNoData,
  emptyMessage,
  noResultsMessage,
  header,
  virtualizeThreshold = DEFAULT_THRESHOLD,
  estimateRowHeight = DEFAULT_ROW_HEIGHT,
  estimateCardHeight = DEFAULT_CARD_HEIGHT,
  maxHeight = 'max-h-[70svh]',
  columnWidths,
  columnLayout,
  onEndReached,
  truncateCells = true,
  containerRef,
  measureRef,
  remeasureToken,
}: TableShellProps<T>) {
  const isDesktop = useIsDesktop();
  const emptyText = hasNoData ? emptyMessage : noResultsMessage;
  const virtualize = items.length > virtualizeThreshold;
  const ownContainerRef = useRef<HTMLDivElement>(null);
  const container = containerRef ?? ownContainerRef;
  // Rendered inside the container so switching between the two desktop layouts
  // re-runs its delegation against the container that is actually mounted.
  const tooltip = useTruncationTooltip(container);
  const overlay = truncateCells ? tooltip : null;

  if (isDesktop && virtualize) {
    return (
      <VirtualTable
        items={items}
        getItemKey={getItemKey}
        renderRow={renderRow}
        header={header}
        estimateRowHeight={estimateRowHeight}
        maxHeight={maxHeight}
        columnWidths={columnWidths}
        columnLayout={columnLayout}
        onEndReached={onEndReached}
        truncateCells={truncateCells}
        containerRef={container}
        measureRef={measureRef}
        overlay={overlay}
        remeasureToken={remeasureToken}
      />
    );
  }

  if (isDesktop) {
    return (
      <StaticTable
        items={items}
        getItemKey={getItemKey}
        renderRow={renderRow}
        header={header}
        columnCount={columnCount}
        emptyText={emptyText}
        columnWidths={columnWidths}
        columnLayout={columnLayout}
        truncateCells={truncateCells}
        containerRef={container}
        measureRef={measureRef}
        overlay={overlay}
      />
    );
  }

  if (virtualize) {
    return (
      <VirtualCards
        items={items}
        getItemKey={getItemKey}
        renderCard={renderCard}
        estimateCardHeight={estimateCardHeight}
        maxHeight={maxHeight}
        onEndReached={onEndReached}
      />
    );
  }

  return (
    <div className="space-y-3">
      {items.length === 0 && (
        <p className="py-12 text-center text-muted-foreground">{emptyText}</p>
      )}
      {items.map((item) => (
        <div key={getItemKey(item)}>{renderCard(item)}</div>
      ))}
    </div>
  );
}

function StaticTable<T>({
  items,
  getItemKey,
  renderRow,
  header,
  columnCount,
  emptyText,
  columnWidths,
  columnLayout,
  truncateCells,
  containerRef,
  measureRef,
  overlay,
}: {
  items: T[];
  getItemKey: (item: T) => string;
  renderRow: (item: T) => ReactElement;
  header: ReactNode;
  columnCount: number;
  emptyText: string;
  /** Fallback classes for the colgroup, until the container is measured. */
  columnWidths: (string | undefined)[];
  columnLayout: ColumnLayoutInput;
  truncateCells?: boolean;
  containerRef?: RefObject<HTMLDivElement | null>;
  measureRef?: RefObject<HTMLDivElement | null>;
  overlay?: ReactNode;
}) {
  const ownScrollRef = useRef<HTMLDivElement>(null);
  const scrollRef = measureRef ?? ownScrollRef;
  const layout = useColumnLayout(scrollRef, columnLayout);
  useTableWidthSync(containerRef, layout?.total);

  return (
    <div
      ref={containerRef}
      data-dt-truncate={truncateCells ? '' : undefined}
      className="overflow-hidden rounded-xl border-2 border-border"
    >
      <div ref={scrollRef} className="overflow-x-auto">
        <table
          className={cn('table-fixed text-sm', !layout && 'w-full')}
          style={layout ? { width: layout.total } : undefined}
        >
          <ColGroup columnWidths={columnWidths} layout={layout} />
          <thead>{header}</thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td
                  colSpan={columnCount}
                  data-truncate="off"
                  className="px-4 py-12 text-center text-muted-foreground"
                >
                  {emptyText}
                </td>
              </tr>
            )}
            {items.map((item) => withKey(renderRow(item), getItemKey(item)))}
          </tbody>
        </table>
      </div>
      {overlay}
    </div>
  );
}

function VirtualTable<T>({
  items,
  getItemKey,
  renderRow,
  header,
  estimateRowHeight,
  maxHeight,
  columnWidths,
  columnLayout,
  onEndReached,
  truncateCells,
  containerRef,
  measureRef,
  overlay,
  remeasureToken,
}: {
  items: T[];
  getItemKey: (item: T) => string;
  renderRow: (item: T) => ReactElement;
  header: ReactNode;
  estimateRowHeight: number;
  maxHeight: string;
  /** Fallback classes for the colgroup, until the container is measured. */
  columnWidths: (string | undefined)[];
  columnLayout: ColumnLayoutInput;
  onEndReached?: () => void;
  truncateCells?: boolean;
  containerRef?: RefObject<HTMLDivElement | null>;
  measureRef?: RefObject<HTMLDivElement | null>;
  overlay?: ReactNode;
  remeasureToken?: string;
}) {
  const ownScrollRef = useRef<HTMLDivElement>(null);
  const scrollRef = measureRef ?? ownScrollRef;
  const headRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimateRowHeight,
    getItemKey: (index) => getItemKey(items[index]),
    overscan: OVERSCAN,
  });
  // Both panes reserve a scrollbar gutter, so the body pane's content width is
  // the header pane's too — one measurement drives both colgroups.
  const layout = useColumnLayout(scrollRef, columnLayout);
  useTableWidthSync(containerRef, layout?.total);

  const virtualItems = virtualizer.getVirtualItems();
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom =
    virtualizer.getTotalSize() -
    (virtualItems.length > 0 ? virtualItems[virtualItems.length - 1].end : 0);

  useEndReached(virtualItems, items.length, onEndReached);

  // A committed reorder or resize can change how tall a row renders; the cached
  // heights would otherwise survive it.
  const measure = virtualizer.measure;
  useEffect(() => {
    if (remeasureToken !== undefined) measure();
  }, [remeasureToken, measure]);

  const body = (
    <tbody>
      {paddingTop > 0 && <tr style={{ height: paddingTop }} />}
      {virtualItems.map((virtualRow) =>
        withMeasure(renderRow(items[virtualRow.index]), {
          key: virtualRow.key,
          index: virtualRow.index,
          measureRef: virtualizer.measureElement,
        }),
      )}
      {paddingBottom > 0 && <tr style={{ height: paddingBottom }} />}
    </tbody>
  );

  const tableClassName = cn('table-fixed text-sm', !layout && 'w-full');
  const tableStyle = layout ? { width: layout.total } : undefined;
  const colgroup = <ColGroup columnWidths={columnWidths} layout={layout} />;

  return (
    <div
      ref={containerRef}
      data-dt-truncate={truncateCells ? '' : undefined}
      className="overflow-hidden rounded-xl border-2 border-border"
    >
      <div
        ref={headRef}
        className="overflow-x-hidden overflow-y-auto [scrollbar-gutter:stable]"
        // The header pane is not scrollable by hand, but the browser still
        // scrolls it to reveal a focused control or a captured pointer during a
        // column resize — which would leave it offset from the body.
        onScroll={() => syncScrollLeft(scrollRef.current, headRef.current)}
      >
        <table className={tableClassName} style={tableStyle}>
          {colgroup}
          <thead>{header}</thead>
        </table>
      </div>
      <div
        ref={scrollRef}
        className={cn('overflow-auto [scrollbar-gutter:stable]', maxHeight)}
        onScroll={() => syncScrollLeft(headRef.current, scrollRef.current)}
      >
        <table className={tableClassName} style={tableStyle}>
          {colgroup}
          {body}
        </table>
      </div>
      {overlay}
    </div>
  );
}

/**
 * Both panes hold the same table width, so an assignment either way settles in
 * one step: writing a `scrollLeft` that already matches fires no event, which
 * is what stops the two handlers from bouncing off each other.
 */
function syncScrollLeft(
  target: HTMLElement | null,
  source: HTMLElement | null,
) {
  if (!target || !source) return;
  if (target.scrollLeft !== source.scrollLeft) {
    target.scrollLeft = source.scrollLeft;
  }
}

/**
 * Writes the table's own width to the DOM after every render.
 *
 * A column resize sets `col.style.width` *and* the table width imperatively, to
 * avoid a `setState` per pointer move. React does not know about either, and it
 * diffs styles against what it last rendered rather than against the DOM — so
 * when the absorbing column swallows the difference and the committed total
 * equals the previous one, React writes nothing and the mid-drag width survives
 * the drop. The `<col>` widths are safe (the dragged one always changes); this
 * is the one property that needs claiming back.
 */
function useTableWidthSync(
  containerRef: RefObject<HTMLElement | null> | undefined,
  total?: number,
) {
  useLayoutEffect(() => {
    const tables = containerRef?.current?.querySelectorAll('table') ?? [];
    for (const table of tables) {
      table.style.width = total === undefined ? '' : `${total}px`;
    }
  });
}

interface ColumnLayout {
  widths: number[];
  total: number;
}

/**
 * Turns the declared column widths into explicit pixels once the container has
 * been measured, so every `<col>` has a definite width and the table is sized
 * to their exact sum. `table-layout: fixed` on a `width: 100%` table spreads
 * any leftover space across every column, which is invisible until somebody
 * drags a column and watches it settle a few pixels off.
 *
 * `null` until the measurement lands — and forever in jsdom, which has no
 * `ResizeObserver` — and the caller then renders the class-based colgroup it
 * always did.
 */
function useColumnLayout(
  ref: RefObject<HTMLElement | null>,
  input?: ColumnLayoutInput,
): ColumnLayout | null {
  const width = useElementWidth(ref);
  if (!input) return null;
  const widths = resolveColumnWidths({ ...input, containerWidth: width });
  if (!widths) return null;
  return { widths, total: widths.reduce((sum, value) => sum + value, 0) };
}

function ColGroup({
  columnWidths,
  layout,
}: {
  columnWidths: (string | undefined)[];
  layout: ColumnLayout | null;
}) {
  return (
    <colgroup>
      {columnWidths.map((width, index) => (
        <col
          key={index}
          className={layout ? undefined : width}
          style={layout ? { width: layout.widths[index] } : undefined}
        />
      ))}
    </colgroup>
  );
}

/** The element's content width in whole pixels, or `0` before it is measured. */
function useElementWidth(ref: RefObject<HTMLElement | null>) {
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      // Round down: a table sized to a container whose width rounded *up*
      // overflows it by a fraction of a pixel and grows a scrollbar.
      setWidth(Math.floor(entry.contentRect.width));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return width;
}

function VirtualCards<T>({
  items,
  getItemKey,
  renderCard,
  estimateCardHeight,
  maxHeight,
  onEndReached,
}: {
  items: T[];
  getItemKey: (item: T) => string;
  renderCard: (item: T) => ReactNode;
  estimateCardHeight: number;
  maxHeight: string;
  onEndReached?: () => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimateCardHeight,
    getItemKey: (index) => getItemKey(items[index]),
    overscan: OVERSCAN,
  });

  const virtualItems = virtualizer.getVirtualItems();
  useEndReached(virtualItems, items.length, onEndReached);

  return (
    <div ref={scrollRef} className={cn('overflow-y-auto', maxHeight)}>
      <div
        className="relative w-full"
        style={{ height: virtualizer.getTotalSize() }}
      >
        {virtualItems.map((virtualRow) => (
          <div
            key={virtualRow.key}
            data-index={virtualRow.index}
            ref={virtualizer.measureElement}
            className="absolute left-0 top-0 w-full pb-3"
            style={{ transform: `translateY(${virtualRow.start}px)` }}
          >
            {renderCard(items[virtualRow.index])}
          </div>
        ))}
      </div>
    </div>
  );
}

function useEndReached(
  virtualItems: { index: number }[],
  count: number,
  onEndReached?: () => void,
) {
  const lastIndex =
    virtualItems.length > 0 ? virtualItems[virtualItems.length - 1].index : -1;

  useEffect(() => {
    if (!onEndReached || lastIndex < 0) return;
    if (lastIndex >= count - 1 - END_REACHED_SLACK) onEndReached();
  }, [lastIndex, count, onEndReached]);
}

function withKey(element: ReactElement, key: string) {
  if (!isValidElement(element)) return element;
  return cloneElement(element as ReactElement<Record<string, unknown>>, {
    key,
  });
}

function withMeasure(
  element: ReactElement,
  {
    key,
    index,
    measureRef,
  }: {
    key: Key;
    index: number;
    measureRef: (node: Element | null) => void;
  },
) {
  if (!isValidElement(element)) return element;
  return cloneElement(element as ReactElement<Record<string, unknown>>, {
    key,
    'data-index': index,
    ref: measureRef,
  });
}
